import { Button } from "@mantine/core";

import { STYLE } from "../../types/constants";
import { Order, Pickup, TranslationOptions } from "../../types/interfaces";
import { useState } from "react";
import SelectInputRef from "../../components/SelectInputRef";
import { useForm } from "react-hook-form";
import { formatWithLeadingZero } from "../../utils/date.utils";
import {
  CarrierType,
  TranslationsAvailable,
  ShippingType,
} from "../../types/enums";
import TextAreaRef from "../../components/TextAreaRef";
import { isIntegratedCarrier } from "../../utils/shipping.utils";
import TextInputRef from "../../components/TextInputRef";

type CreatePickupProps = {
  onSubmit: (
    orderId: string,
    orderReference: string,
    data: {
      clinicClosingTime: string;
      packageReadyTime: string;
      date: string;
      comment: string | null;
      selectedCarrier: CarrierType;
      trackingNumber?: string;
    }
  ) => void;
  onEdit: (data: {
    clinicClosingTime: string;
    packageReadyTime: string;
    date: string;
    comment: string | null;
    pickupCarrier: CarrierType;
    trackingNumber?: string;
  }) => void;
  orderId: string;
  orderReference: string;
  lang: TranslationsAvailable;
  pickupCarrier: CarrierType;
  actualPickup?: Pickup;
  pickupAvailabilities: {
    latestTimeOptions: string[];
    readyTimeOptions: string[];
    pickupDate: string;
  }[];
  connectedUserEmail?: string;
  disableTimeSelection?: boolean;
};

const CreatePickup = ({
  onSubmit,
  onEdit,
  orderId,
  orderReference,
  lang,
  pickupCarrier,
  actualPickup,
  pickupAvailabilities,
  connectedUserEmail,
  disableTimeSelection,
}: CreatePickupProps) => {
  const isPickupCarrierIntegratedCarrier = isIntegratedCarrier(
    pickupCarrier,
    ShippingType.PICKUP,
    connectedUserEmail
  );

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    actualPickup?.scheduledDate
      ? new Date(actualPickup?.scheduledDate)
      : isPickupCarrierIntegratedCarrier
        ? undefined
        : new Date()
  );

  const rhf = useForm({
    defaultValues: {
      date: null,
      comment: actualPickup?.comment ?? null,
      trackingNumber: actualPickup?.shipping?.trackingNumber ?? null,
      clinicClosingTime: null,
      packageReadyTime: null,
    },
  });

  const getDateInCorrectFormat = (inputDate: string) => {
    const dt = new Date(inputDate);
    const month = formatWithLeadingZero(dt.getUTCMonth() + 1);
    const date = formatWithLeadingZero(dt.getUTCDate());
    return `${dt.getUTCFullYear()}-${month}-${date}`;
  };

  return (
    <>
      {isPickupCarrierIntegratedCarrier && (
        <div style={{ paddingBottom: "1rem" }}>
          <SelectInputRef
            name="date"
            label={
              lang === TranslationsAvailable.FR
                ? "Date du ramassage"
                : "Date of pickup"
            }
            errors={rhf.formState.errors}
            control={rhf.control}
            data={pickupAvailabilities.map(availability => {
              return {
                label:
                  lang === TranslationsAvailable.FR
                    ? new Date(availability.pickupDate).toLocaleDateString()
                    : new Date(availability.pickupDate).toDateString(),
                value: availability.pickupDate,
              };
            })}
          />
        </div>
      )}
      {rhf.watch("date") &&
        pickupCarrier === CarrierType.FEDEX_TNT &&
        !disableTimeSelection && (
          <>
            <div style={{ paddingBottom: "1rem" }}>
              <SelectInputRef
                name="packageReadyTime"
                label={
                  lang === TranslationsAvailable.FR
                    ? "Heure où le colis sera prêt"
                    : "Package ready time"
                }
                errors={rhf.formState.errors}
                control={rhf.control}
                data={
                  pickupAvailabilities.find(
                    availability =>
                      availability.pickupDate === rhf.getValues("date")
                  )?.readyTimeOptions ?? []
                }
              />
            </div>
            <div style={{ paddingBottom: "1rem" }}>
              <SelectInputRef
                name="clinicClosingTime"
                label={
                  lang === TranslationsAvailable.FR
                    ? "Heure de fermeture du cabinet"
                    : "Clinic closing time"
                }
                errors={rhf.formState.errors}
                control={rhf.control}
                data={
                  pickupAvailabilities.find(
                    availability =>
                      availability.pickupDate === rhf.getValues("date")
                  )?.latestTimeOptions ?? []
                }
              />
            </div>
          </>
        )}
      {!isPickupCarrierIntegratedCarrier && (
        <>
          <div style={{ paddingBottom: "1rem" }}>
            <input
              type="date"
              value={selectedDate.toISOString().split("T")[0]}
              style={{ width: "100%" }}
              onChange={async e => {
                if (!e.target.value) {
                  return;
                }
                const calendarSelectedDate = new Date(e.target.value);
                setSelectedDate(calendarSelectedDate);
              }}
            />
          </div>
          <TextInputRef
            label="Tracking number"
            name="trackingNumber"
            errors={rhf.formState.errors}
            control={rhf.control}
            required={true}
          />
        </>
      )}
      <div style={{ paddingBottom: "1rem" }}>
        <TextAreaRef
          name="comment"
          label={lang === TranslationsAvailable.FR ? "Commentaire" : "Comment"}
          errors={rhf.formState.errors}
          control={rhf.control}
          required={false}
        />
      </div>
      <Button
        style={{ backgroundColor: STYLE.primary, width: "100%" }}
        onClick={rhf.handleSubmit(data => {
          const pickupDate = isPickupCarrierIntegratedCarrier
            ? data.date
            : selectedDate || data.date;
          return actualPickup
            ? onEdit({
                date: getDateInCorrectFormat(pickupDate),
                clinicClosingTime: data.clinicClosingTime,
                packageReadyTime: data.packageReadyTime,
                comment: data.comment,
                pickupCarrier: pickupCarrier,
                trackingNumber: data.trackingNumber,
              })
            : onSubmit(orderId, orderReference, {
                date: getDateInCorrectFormat(pickupDate),
                clinicClosingTime: data.clinicClosingTime,
                packageReadyTime: data.packageReadyTime,
                comment: data.comment,
                selectedCarrier: pickupCarrier,
                trackingNumber: data.trackingNumber,
              });
        })}
      >
        {lang === TranslationsAvailable.FR
          ? "Valider le ramassage"
          : "Create pickup"}
      </Button>
    </>
  );
};

export default CreatePickup;
